"use client"

import React, { useEffect, useMemo, useRef, useState, useTransition } from "react"
import { debounce } from "next/dist/server/utils"
import Link from "next/link"
import { loadProducts } from "@/actions/productActions"
import { deleteQuoteOrQuoteRequestAction } from "@/actions/quoteActions"
import { QuoteRequestRO, QuoteRO } from "@ub/quoting-client"
import { LoaderCircle, PlusIcon, SearchIcon } from "lucide-react"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { ReducedProductData } from "@/lib/storefront/product/product"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/components/alert-dialog"
import { DataPagination } from "@/components/ui/components/data-pagination"
import AccountQuoteMiniCard from "@/components/account/quotes/AccountQuoteMiniCard"
import { UbAccountQuotesViewStoryblok } from "@/components/storyblok/component-types"

interface AccountQuotesClientProps {
  accountContext: AccountContext
  allItems: Array<QuoteRO | QuoteRequestRO>
  blok: UbAccountQuotesViewStoryblok
}

interface QuoteCardProps {
  quote: QuoteRO | QuoteRequestRO
  isoLocale: string
  sbLocale: string
  isQuoteRequest: boolean
  blok: UbAccountQuotesViewStoryblok
  onRemove?: (quote: QuoteRO | QuoteRequestRO) => void
}

function QuoteCard(props: QuoteCardProps) {
  const [products, setProducts] = useState<ReducedProductData[]>([])
  const productSkus = useMemo(
    () =>
      props.isQuoteRequest
        ? (props.quote as QuoteRequestRO).items?.map((item) => (item as any).productSKU) || []
        : (props.quote as QuoteRO).items?.map((item) => (item as any).productSKU) || [],
    [props.isQuoteRequest, props.quote]
  )

  useEffect(() => {
    if (productSkus.length > 0) {
      // stops fetching server on first render
      loadProducts(productSkus.slice(0, 8)).then((loadedProducts) =>
        setProducts(loadedProducts.filter((l) => l != undefined))
      )
    }
  }, [productSkus])

  return (
    <Link
      href={getLinkWithPrefixFromURLAndLocale(`/account/quote/${props.quote.id}`, props.sbLocale)}
      prefetch={false}
      className={"group/entry flex w-full flex-col gap-1 rounded-sm border border-gray-200 bg-white p-2 hover:shadow"}
    >
      <AccountQuoteMiniCard
        quote={props.quote}
        isQuoteRequest={props.isQuoteRequest}
        isoLocale={props.isoLocale}
        sbLocale={props.sbLocale}
        productSkus={productSkus}
        products={products}
        onRemove={props.onRemove}
        label_quote_items={props.blok.label_quote_items ?? ""}
        label_quote_process_number={props.blok.label_quote_process_number ?? ""}
        label_quote_status_rejected={props.blok.label_quote_status_rejected ?? ""}
        label_quote_reference={props.blok.label_quote_reference ?? ""}
        label_quote_status={props.blok.label_quote_status ?? ""}
        label_quote_status_expired={props.blok.label_quote_status_expired ?? ""}
        label_quote_status_new={props.blok.label_quote_status_new ?? ""}
        label_quote_status_sent={props.blok.label_quote_status_sent ?? "Gesendet"}
        label_quote_status_responded={props.blok.label_quote_status_responded ?? ""}
      />
    </Link>
  )
}

export default function AccountQuoteListsClient(props: AccountQuotesClientProps) {
  const [allItems, setAllItems] = useState(props.allItems)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const itemsPerPage = 8
  const debouncedSetSearchQuery = debounce(setSearchQuery, 500)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [toBeDeleted, setToBeDeleted] = useState<QuoteRO | QuoteRequestRO | null>(null)
  const [loadingDelete, deleteTransaction] = useTransition()
  const searchField = useRef<HTMLInputElement>(null)

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (searchQuery === "") {
      return allItems
    }
    const reg = new RegExp(searchQuery, "i")
    return allItems.filter(
      (item) =>
        (item.displayName ?? "").match(reg) || (item.number ?? "").match(reg) || (item.description ?? "").match(reg)
    )
  }, [searchQuery, allItems])

  const totalPages = useMemo(() => Math.ceil(filteredItems.length / itemsPerPage), [filteredItems])
  const startIndex = useMemo(() => (currentPage - 1) * itemsPerPage, [currentPage, itemsPerPage])
  const endIndex = useMemo(() => startIndex + itemsPerPage, [startIndex, itemsPerPage])

  const currentItems = useMemo(() => filteredItems.slice(startIndex, endIndex), [filteredItems, startIndex, endIndex])

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  async function handleDelete(quote: QuoteRO | QuoteRequestRO) {
    setToBeDeleted(quote)
    setConfirmDialogOpen(true)
  }
  async function resetDelete() {
    setConfirmDialogOpen(false)
    setToBeDeleted(null)
  }
  async function handleConfirmedDelete() {
    deleteTransaction(async () => {
      if (toBeDeleted) {
        await deleteQuoteOrQuoteRequestAction(
          props.accountContext.customerData,
          props.accountContext.userData,
          toBeDeleted
        ).then(() => setAllItems(allItems.filter((item) => item.id !== toBeDeleted.id)))
      }
      await resetDelete()
    })
  }

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        <div className="rounded-sm border border-gray-300 bg-gray-200">
          <div className="grid grid-rows-1 gap-2 py-4 lg:grid-cols-[min-content,_auto,_max-content] lg:grid-rows-[min-content_min-content]">
            <div className={"col-span-full mb-2 flex w-full justify-center border-b border-gray-300 px-4 pb-4"}>
              <div
                className={
                  "grid size-fit grid-cols-1 grid-rows-[1fr_min-content_1fr_min-content_1fr] items-center  text-xs xl:grid-cols-[1fr_min-content_1fr_min-content_1fr] xl:grid-rows-1 xl:gap-3 xl:text-sm 2xl:gap-4"
                }
              >
                {Array(5)
                  .fill("")
                  .map((_, i) =>
                    i % 2 === 0 ? (
                      <div
                        key={i}
                        className="flex items-center rounded-lg border border-gray-300 bg-white px-6 py-4 xl:p-2 2xl:p-3"
                      >
                        <span className="mr-3 flex size-fit items-center justify-center text-3xl leading-none text-primary-hover xl:text-4xl">
                          {i / 2 + 1}
                        </span>
                        <p className={"hyphens-auto text-wrap"}>{props.blok[`label_quote_step${i / 2 + 1}`]}</p>
                      </div>
                    ) : (
                      <div key={i} className={"flex items-center justify-center"}>
                        <div className={"_2xl:h-11 h-8 w-fit overflow-hidden max-xl:rotate-90"}>
                          <span
                            className={
                              "_2xl:-top-9 _2xl:text-[6rem] relative -top-6 inline-block text-[4rem] font-extralight leading-none text-primary-hover"
                            }
                          >
                            {"›"}
                          </span>
                        </div>
                      </div>
                    )
                  )}
              </div>
            </div>
            <div className={"flex justify-center px-4 lg:order-4"}>
              <p className="my-auto text-sm text-gray-800 lg:ml-auto">
                {(props.blok.label_quote_created_quotes ?? "").replace("%", String(allItems.length))}
              </p>
            </div>
            <div className="relative h-fit w-full px-4 lg:w-80">
              {searchField.current && searchField.current.value.trim().length > 0 ? (
                <button
                  onClick={() => {
                    if (searchField.current) {
                      searchField.current.value = ""
                    }
                    setSearchQuery("")
                  }}
                  className={"absolute right-6 top-1/2 size-5 -translate-y-1/2 text-gray-400"}
                >
                  <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
                </button>
              ) : (
                <div className={"pointer-events-none absolute right-6 top-1/2 size-5 -translate-y-1/2 text-gray-400"}>
                  <SearchIcon strokeWidth={2} className="size-5" />
                </div>
              )}
              <input
                type="text"
                ref={searchField}
                placeholder={props.blok.placeholder_search_quotes}
                onChange={(e) => debouncedSetSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className={"max-lg:hidden"}></div>
          </div>
        </div>
      </div>

      {/* Search box and Quotes list container */}
      <div className="grid max-h-full grid-flow-row gap-2 lg:grid-cols-2">
        {/* Quotes list */}
        {currentItems.map((item) => (
          <QuoteCard
            key={item.id}
            quote={item}
            isoLocale={props.accountContext.isoLocale}
            sbLocale={props.accountContext.sbLocale}
            isQuoteRequest={item.type === "QuoteRequest"}
            onRemove={handleDelete}
            blok={props.blok}
          />
        ))}
      </div>

      <DataPagination initialPage={currentPage} totalPages={totalPages} onPageChange={(page) => setCurrentPage(page)} />
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen} defaultOpen={false}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{props.blok.delete_alert_title}</AlertDialogTitle>
            <AlertDialogDescription>{props.blok.delete_alert_message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetDelete} disabled={loadingDelete}>
              {props.blok.common_cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedDelete} disabled={loadingDelete}>
              {loadingDelete ? <LoaderCircle className={"motion-safe:animate-spin"} /> : props.blok.common_confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
